import { ALGOLIA_TYPES, STORES } from '../Constants';
import { SearchResultsProps } from '../../navigation/header-new/search/SearchResultsDropdown';
import { HitProp, SearchResponsePerIndex } from '../types';
import { Product } from '../../../types/category/category';

type AlgoliaKeys = keyof typeof ALGOLIA_TYPES;
type IndexName = `${typeof env}_${typeof store}_${AlgoliaKeys}`;

const GATSBY_STORE_DELIMITER = '-';
const store = process.env.GATSBY_STORE ? process.env.GATSBY_STORE.split(GATSBY_STORE_DELIMITER)[0] as keyof typeof STORES : 'drukwerkdeal';
const env = process.env.GATSBY_ACTIVE_ENV === 'production' ? 'prod' : 'dev';

export const INDICES = Object.entries(ALGOLIA_TYPES).reduce(
  (indices, [indexName, indexValue]) => ({ ...indices, [indexName]: `${env}_${store}_${indexValue}` }),
  {} as Record<keyof typeof ALGOLIA_TYPES, IndexName>,
);

// method to be used to filter searchResponses
export const filterSearchResponses = (searchResults: SearchResponsePerIndex[]) => {
  const filteredSearchResponses: SearchResultsProps = {
    productResults: [],
    categoryResults: [],
    contentPageResults: [],
    productQueryId: null,
    categoryQueryId: null,
    contentPageQueryId: null,
    productIndex: null,
    categoryIndex: null,
    contentPageIndex: null,
    totalProductsCount: 0,
  };

  searchResults.forEach((searchResult) => {
    if (!searchResult?.searchResults?.hits) return;

    switch (searchResult.name) {
      case INDICES.PDP_INDEX:
        filteredSearchResponses.productResults = searchResult.searchResults.hits.map((result) => {
          if (result.path) {
            // eslint-disable-next-line no-param-reassign, no-underscore-dangle
            result.highlightedTextProductName = result._highlightResult?.productName?.value || result.productName;
          }
          return result;
        }).filter((result) => Boolean(result.path));
        filteredSearchResponses.productIndex = searchResult.searchResults.index;
        filteredSearchResponses.productQueryId = searchResult.searchResults.queryID;
        filteredSearchResponses.totalProductsCount = searchResult.searchResults.nbHits;
        break;
      case INDICES.PROD_CAT_INDEX:
        filteredSearchResponses.categoryResults = searchResult.searchResults.hits.map((result) => {
          if (result.path) {
            // eslint-disable-next-line no-param-reassign, no-underscore-dangle
            result.highlightedTitle = result._highlightResult?.categoryName?.value || result.title;
          }
          return result;
        }).filter((result) => Boolean(result.path));
        filteredSearchResponses.categoryIndex = searchResult.searchResults.index;
        filteredSearchResponses.categoryQueryId = searchResult.searchResults.queryID;
        break;
      case INDICES.CONTENT_PAGES_INDEX:
        filteredSearchResponses.contentPageResults = searchResult.searchResults.hits.map((result) => {
          if (result.path) {
            // eslint-disable-next-line no-param-reassign, no-underscore-dangle
            result.highlightedTitle = result._highlightResult?.title?.value || result.title;
          }
          return result;
        }).filter((results) => Boolean(results.path));
        filteredSearchResponses.contentPageIndex = searchResult.searchResults.index;
        filteredSearchResponses.contentPageQueryId = searchResult.searchResults.queryID;
        break;
      default:
        break;
    }
  });

  return filteredSearchResponses;
};

export const transformAlgoliaHitsToCategoryProducts = (items: HitProp[]): Product[] => items.map(
  (item, index: number) => {
    const {
      productImages: {
        featuredImage,
        firstImageFromGallery = {},
      },
    } = item;

    return {
      __typename: 'ContentfulProductContent',
      id: item.objectID,
      title: item.productName,
      productName: item.productName,
      itemIndex: index + 1,
      name: item.productName,
      path: item.path,
      slug: item.slug,
      node_locale: item.locale,
      featuredImage: {
        title: item.productName,
        url: featuredImage?.src,
        contentful_id: '',
        width: 1800,
        height: 1800,
      },
      description: item.featuredDescription,
      image: {
        title: firstImageFromGallery?.title,
        url: firstImageFromGallery?.src || featuredImage?.src,
        width: firstImageFromGallery?.width || 1800,
        height: firstImageFromGallery?.height,
      },
      parentProduct: {
        productName: item.parentProductName,
      },
    };
  },
);
